import { type MouseEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import { t } from "i18next";

import { Menu as MenuIcon, Notifications } from "@mui/icons-material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import DeleteIcon from "@mui/icons-material/Delete";
import LogoutIcon from "@mui/icons-material/Logout";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
  Toolbar,
  Typography,
  useMediaQuery
} from "@mui/material";
import { type Theme } from "@mui/material/styles";

import { theme } from "@/src/theme/theme";
import { getAuctionStatusChipStyles } from "@/src/theme/utils/auctionColors";

import DraftAuctionModal from "@/src/components/organisms/DraftAuctionModal/DraftAuctionModal";
import DeleteDraftAuctionConfirmationModal from "@/src/components/organisms/Header/DeleteDraftAuctionConfirmationModal";

import { LoginSource } from "@/src/auth/auth";
import { useAuth } from "@/src/auth/hooks/useAuth";
import {
  ROUTES,
  getAuctionEditRoute,
  getAuctionEditRouteFromPathname,
  getHomeRoute,
  isCaravanRoute,
  isMotoRoute,
  isOnWizardRoute
} from "@/src/constants/routes";
import { useGetAllAuctions } from "@/src/hooks/consumerAuctions";
import { useClearAuctionState } from "@/src/hooks/useClearAuctionState";
import { useRemoveConsumerAuction } from "@/src/hooks/useRemoveConsumerAuction";
import { getAuction } from "@/src/queries/consumer/consumerFunctions";
import { useGetNotifications } from "@/src/queries/notifications/notificationQueries";
import {
  AuctionState,
  ConsumerAuctionDTO,
  VehicleType
} from "@/src/types/types";
import { filterActiveAuctions } from "@/src/utils/auctionItemUtils";

import LanguageSwitcher from "../../../localizations/languageSwitcher";
import { notificationAtom } from "../../../state/notificationAtom";
import { BaanaLogo } from "../../atoms";
import NotificationMenu from "../NotificationCenter/NotificationMenu";
import HeaderMenuItem from "./HeaderMenuItem";
import UserInfo from "./UserInfo";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useSetAtom } from "jotai";

const consumerHeaderButtonHoverShadowSx = {
  boxShadow:
    "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)"
} as const;

const consumerHeaderLoginButtonSx = (theme: Theme) => ({
  fontSize: "0.875rem",
  fontWeight: theme.typography.fontWeightBold,
  backgroundColor: theme.palette.common.white,
  color: theme.palette.primary.main,
  height: "36px",
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
    ...consumerHeaderButtonHoverShadowSx
  }
});

const consumerHeaderDealerSiteButtonSx = (theme: Theme) => ({
  fontSize: "0.875rem",
  fontWeight: theme.typography.fontWeightBold,
  mr: 2,
  height: "36px",
  "&:hover": consumerHeaderButtonHoverShadowSx
});

const ConsumerHeader = () => {
  const navigate = useNavigate();
  const setNotification = useSetAtom(notificationAtom);

  const sendDataToGTM = useGTMDispatch();
  const { isLoading: isLoadingUserAuctions, data: allAuctions } =
    useGetAllAuctions();

  const location = useLocation();
  const isInUnauthWizard =
    location.pathname === ROUTES.CONSUMER.AUCTION_CREATE ||
    location.pathname === ROUTES.CONSUMER.AUCTION_CREATE_MOTO ||
    location.pathname === ROUTES.CONSUMER.AUCTION_CREATE_CARAVAN ||
    location.pathname === ROUTES.CONSUMER.AUCTION_EDIT ||
    location.pathname === ROUTES.CONSUMER.AUCTION_EDIT_MOTO ||
    location.pathname === ROUTES.CONSUMER.AUCTION_EDIT_CARAVAN;
  const isLargeScreen = useMediaQuery("(min-width:910px)");
  const [consumerMenuToggleButtonElement, setConsumerMenuToggleButton] =
    useState<null | HTMLElement>(null);
  const [auctionsMenuAnchor, setAuctionsMenuAnchor] =
    useState<null | HTMLElement>(null);
  const menuOpen = Boolean(consumerMenuToggleButtonElement);
  const auctionsMenuOpen = Boolean(auctionsMenuAnchor);
  const currentUrl = location.pathname;

  const handleMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setConsumerMenuToggleButton(event.currentTarget);
  };

  const handleMenuClose = () => {
    setConsumerMenuToggleButton(null);
  };

  const handleAuctionsMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAuctionsMenuAnchor(event.currentTarget);
  };

  const handleAuctionsMenuClose = () => {
    setAuctionsMenuAnchor(null);
  };

  // Close menus when switching between desktop and mobile to prevent invalid anchorEl
  useEffect(() => {
    setConsumerMenuToggleButton(null);
    setAuctionsMenuAnchor(null);
  }, [isLargeScreen]);

  const {
    login,
    logout,
    isAuthenticated,
    isAuthenticating,
    isEmailVerified,
    user
  } = useAuth();

  const activeAuctionsCount = useMemo(() => {
    if (!allAuctions || !isAuthenticated) return 0;
    return filterActiveAuctions(allAuctions).length;
  }, [allAuctions, isAuthenticated]);

  const { data: notifications } = useGetNotifications(isAuthenticated);

  const unreadNotifications = notifications?.filter(
    ({ isRead }) => !isRead
  ).length;

  const { clearAllAuctionState } = useClearAuctionState();
  const { removeConsumerAuction } = useRemoveConsumerAuction();
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftAuction, setDraftAuction] = useState<ConsumerAuctionDTO | null>(
    null
  );
  const [deleteModalAuctionId, setDeleteModalAuctionId] = useState<
    number | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const handleNavigation = (url: string) => {
    navigate(url);
  };

  // Find draft auction
  const draftAuctionFromList = useMemo(() => {
    if (!allAuctions) return null;
    return (
      allAuctions.find(
        (auction) => auction.auctionState === AuctionState.DRAFT
      ) || null
    );
  }, [allAuctions]);

  // Fetch full draft auction details when modal opens
  useEffect(() => {
    if (showDraftModal && draftAuctionFromList?.id) {
      const fetchFullDetails = async () => {
        try {
          const fullDetails = await getAuction({
            auctionId: draftAuctionFromList.id
          });
          setDraftAuction(fullDetails);
        } catch (error) {
          console.error("Failed to fetch full draft auction details:", error);
          setDraftAuction(draftAuctionFromList);
        }
      };
      fetchFullDetails();
    }
  }, [showDraftModal, draftAuctionFromList]);

  const handleNewAuctionClick = async () => {
    if (!isEmailVerified) return;

    // If there's a draft, show modal instead of directly navigating
    if (draftAuctionFromList) {
      setShowDraftModal(true);
      handleAuctionsMenuClose();
      return;
    }

    // Clear wizard state when creating a new auction to ensure fresh start
    clearAllAuctionState();
    handleAuctionsMenuClose();
    const editPath = getAuctionEditRouteFromPathname(currentUrl);
    handleNavigation(editPath);
  };

  const handleContinueDraft = () => {
    if (draftAuction) {
      const vehicleType =
        draftAuction.auctionItem?.vehicle?.vehicleType ?? VehicleType.CAR;
      const editPath = getAuctionEditRoute(vehicleType);
      handleNavigation(editPath);
    }
  };

  const [isRemovingDraft, setIsRemovingDraft] = useState(false);

  const handleStartNew = async () => {
    if (isRemovingDraft) return;
    setIsRemovingDraft(true);

    const draftToDelete = draftAuction || draftAuctionFromList;

    if (draftToDelete?.id) {
      try {
        await removeConsumerAuction(draftToDelete.id);
        setNotification({
          text: t("myAuctions.auctionDeleted"),
          variant: "success"
        });
      } catch (error) {
        console.error("Error deleting draft:", error);
        setNotification({
          text: t("myAuctions.hasErrorWithRemoval"),
          variant: "error"
        });
        setIsRemovingDraft(false);
        return;
      }
    } else {
      clearAllAuctionState();
    }

    setShowDraftModal(false);
    const editPath = getAuctionEditRouteFromPathname(currentUrl);
    handleNavigation(editPath);
    setIsRemovingDraft(false);
  };

  const handleDeleteClick = (e: MouseEvent, auctionId: number) => {
    e.stopPropagation();
    setDeleteModalAuctionId(auctionId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalAuctionId) return;

    setIsDeleting(true);
    try {
      await removeConsumerAuction(deleteModalAuctionId);
      setNotification({
        text: t("myAuctions.auctionDeleted"),
        variant: "success"
      });
      setDeleteModalAuctionId(null);
      handleAuctionsMenuClose();

      if (isOnWizardRoute(currentUrl)) {
        navigate(getAuctionEditRouteFromPathname(currentUrl));
      }
    } catch (error) {
      console.error("Error removing auction:", error);
      setNotification({
        text: t("myAuctions.hasErrorWithRemoval"),
        variant: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Authenticated & unauthenticated header items.
  // Logo "home" navigates to the home for current vertical (caravan -> /caravan, moto -> /moto, car -> /).
  const getHomeRouteForUrl = (): string => {
    if (isCaravanRoute(currentUrl)) return getHomeRoute(VehicleType.CARAVAN);
    if (isMotoRoute(currentUrl)) return getHomeRoute(VehicleType.MOTO);
    return getHomeRoute(VehicleType.CAR);
  };
  const homeRoute = getHomeRouteForUrl();

  const consumerHeaderLogoButton = (
    <Box
      id="consumerLogoButton"
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        pt: 1
      }}
      onClick={() => {
        handleNavigation(homeRoute);
      }}
    >
      <Box sx={{ width: "6rem" }}>
        <BaanaLogo color={theme.palette.semantic.primary.contrastText} />
      </Box>
    </Box>
  );

  const headerItemArticles = isAuthenticated ? (
    <MenuItem
      id="consumerMenuItemArticles"
      onClick={() => {
        handleMenuClose();
        navigate("/blog");
        sendDataToGTM({
          event: "articles_header_clicked",
          user_type: "consumer"
        });
      }}
    >
      {isLargeScreen && isAuthenticated && (
        <ListItemIcon>
          <AutoStoriesIcon fontSize="small" />
        </ListItemIcon>
      )}
      {t("consumer.menu.articles")}
    </MenuItem>
  ) : (
    <HeaderMenuItem
      id="consumerHeaderMenuItemArticles"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/blog");
        sendDataToGTM({
          event: "articles_header_clicked",
          user_type: "consumer"
        });
      }}
      isActive={currentUrl === "/blog"}
      label={t("consumer.menu.articles")}
    />
  );

  const headerItemContactInfo = isAuthenticated ? (
    <MenuItem
      id="consumerMenuContactInfoMenuItem"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/contact-info");
      }}
    >
      {isLargeScreen && (
        <ListItemIcon>
          <ContactPageIcon fontSize="small" />
        </ListItemIcon>
      )}
      <ListItemText>{t("consumer.menu.contactInfo")}</ListItemText>
    </MenuItem>
  ) : (
    <HeaderMenuItem
      id="consumerHeaderMenuItemContactInfo"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/contact-info");
      }}
      isActive={currentUrl === "/contact-info"}
      label={t("consumer.menu.contactInfo")}
    />
  );

  const headerItemFAQ = isAuthenticated ? (
    <MenuItem
      id="consumerMenuItemFAQ"
      onClick={() => {
        handleMenuClose();
        navigate("/faq");
      }}
    >
      {isLargeScreen && (
        <ListItemIcon>
          <QuestionAnswerIcon fontSize="small" />
        </ListItemIcon>
      )}
      {t("consumer.menu.faq")}
    </MenuItem>
  ) : (
    <HeaderMenuItem
      id="consumerHeaderMenuItemFAQPage"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/faq");
      }}
      isActive={currentUrl === "/faq"}
      label={t("consumer.menu.faq")}
    />
  );

  // Unauthorized header items
  const headerItemNavToDealerSite =
    !isInUnauthWizard &&
    (isLargeScreen ? (
      <Button
        id="consumerHeaderButtonToDealerSite"
        onClick={() => {
          setNotification(null);
          // Hard page reload needed to refresh the Alma Tunnus component
          localStorage.setItem("USAGE_MODE", "dealer");
          window.location.href = "/dealer";
        }}
        variant="outlined"
        color="inherit"
        sx={consumerHeaderDealerSiteButtonSx}
      >
        {t("consumer.menu.toDealerView")}
      </Button>
    ) : (
      <HeaderMenuItem
        id="consumerHeaderMenuItemNavToDealerSite"
        onClick={() => {
          setNotification(null);
          // Hard page reload needed to refresh the Alma Tunnus component
          localStorage.setItem("USAGE_MODE", "dealer");
          window.location.href = "/dealer";
        }}
        label={t("consumer.menu.toDealerView")}
      />
    ));

  // Removed headerAuctionButton - replaced by consumerOwnAuctionsButton dropdown

  const headerItemLogin = !isInUnauthWizard && (
    <Button
      id={
        isLargeScreen
          ? "consumerHeaderButtonLogin"
          : "consumerHeaderMenuItemLogin"
      }
      onClick={() => {
        sendDataToGTM({
          event: "login_header_clicked",
          user_type: "consumer"
        });
        if (isLargeScreen) {
          handleMenuClose();
        }
        setNotification(null);
        login(
          "consumer",
          isLargeScreen
            ? LoginSource.CONSUMER_HEADER_LOGIN
            : LoginSource.CONSUMER_MENU
        );
      }}
      variant="contained"
      sx={consumerHeaderLoginButtonSx}
    >
      {t("consumer.menu.login")}
    </Button>
  );

  const headerMobileItemNotification = (
    <MenuItem
      id="consumerMenuItemNotifications"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/notifications");
      }}
    >
      {t("consumer.menu.notifications")}
      <Badge
        variant="standard"
        color="secondary"
        badgeContent={unreadNotifications}
      >
        <Notifications
          sx={{ ml: 1, color: theme.palette.text.disabled }}
          fontSize="small"
        />
      </Badge>
    </MenuItem>
  );

  const headerItemUserInfo = (
    <Box sx={{ pl: isLargeScreen ? 0 : 2, py: isLargeScreen ? 0 : 1 }}>
      <UserInfo
        firstName={user?.firstName}
        lastName={user?.lastName}
        email={user?.email}
        companyName={user?.business?.name}
      />
    </Box>
  );

  // Group auctions by state - only active and draft
  const groupedAuctions = useMemo(() => {
    if (!allAuctions || allAuctions.length === 0) return null;

    const grouped: {
      active: ConsumerAuctionDTO[];
      draft: ConsumerAuctionDTO[];
      accepted: ConsumerAuctionDTO[];
    } = {
      active: [],
      draft: [],
      accepted: []
    };

    allAuctions.forEach((auction) => {
      // Check if ENDED auction has pending offer (has offerEndTime or acceptedOffer)
      const hasPendingOffer =
        auction.auctionState === AuctionState.ENDED &&
        (!!auction.offerEndTime || !!auction.acceptedOffer);

      if (auction.auctionState === AuctionState.STARTED || hasPendingOffer) {
        grouped.active.push(auction);
      } else if (auction.auctionState === AuctionState.DRAFT) {
        grouped.draft.push(auction);
      } else if (auction.auctionState === AuctionState.OFFER_ACCEPTED) {
        grouped.accepted.push(auction);
      }
    });

    return grouped;
  }, [allAuctions]);

  const getAuctionLabel = (auction: ConsumerAuctionDTO) => {
    const regNo = auction.auctionItem?.vehicle?.regNo || "";
    const make = auction.auctionItem?.vehicle?.make || "";
    const model = auction.auctionItem?.vehicle?.model || "";

    if (regNo) {
      const vehicleInfo = make || model ? ` - ${make} ${model}` : "";
      return `${regNo}${vehicleInfo}`;
    }

    const makeModel = `${make} ${model}`.trim();
    return makeModel || t("myAuctions.auction");
  };

  const getStateLabel = (auction: ConsumerAuctionDTO): string => {
    const state = auction.auctionState;

    // Check if ENDED auction has pending offer
    const hasPendingOffer =
      state === AuctionState.ENDED &&
      (!!auction.offerEndTime || !!auction.acceptedOffer);

    if (hasPendingOffer) {
      // Check if there are any negotiation messages for any offer
      // negotiationMessages is an object where keys are bidIds (as strings) and values are message arrays
      const hasNegotiationMessages =
        auction.negotiationMessages &&
        Object.keys(auction.negotiationMessages).length > 0 &&
        Object.values(auction.negotiationMessages).some(
          (messages) =>
            messages && Array.isArray(messages) && messages.length > 0
        );

      if (hasNegotiationMessages) {
        return t("myAuctions.inNegotiation");
      } else {
        return t("myAuctions.pendingOffer");
      }
    }

    switch (state) {
      case AuctionState.STARTED:
        return t("auctionStates.seller.STARTED");
      case AuctionState.DRAFT:
        return t("auctionStates.seller.DRAFT");
      case AuctionState.OFFER_ACCEPTED:
        return t("auctionStates.seller.OFFER_ACCEPTED");
      case AuctionState.SOLD:
        return t("auctionStates.seller.SOLD");
      case AuctionState.ENDED:
        return t("auctionStates.seller.ENDED");
      default:
        return state;
    }
  };

  const hasAuctionPendingOffer = (auction: ConsumerAuctionDTO): boolean =>
    auction.auctionState === AuctionState.ENDED &&
    (!!auction.offerEndTime || !!auction.acceptedOffer);

  const getAuctionChipSx = (auction: ConsumerAuctionDTO) => ({
    height: 20,
    fontSize: (theme: Theme) => theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightSemibold,
    ...getAuctionStatusChipStyles(
      theme,
      auction.auctionState,
      hasAuctionPendingOffer(auction)
    )
  });

  const consumerOwnAuctionsButton = isAuthenticated ? (
    <Button
      id="consumerHeaderButtonMyAuctions"
      onClick={handleAuctionsMenuOpen}
      variant="outlined"
      color="primary"
      endIcon={<ArrowDropDownIcon />}
      sx={{
        fontSize: isLargeScreen ? "0.875rem" : "0.75rem",
        fontWeight: theme.typography.fontWeightBold,
        backgroundColor: theme.palette.common.white,
        color: theme.palette.semantic.primary.main,
        borderColor: theme.palette.semantic.primary.main,
        borderWidth: 1,
        "&:hover": {
          backgroundColor: theme.palette.common.white,
          borderColor: theme.palette.semantic.primary.dark,
          borderWidth: 1,
          ...consumerHeaderButtonHoverShadowSx
        },
        mr: isLargeScreen ? 2 : 1,
        p: 1,
        padding: "0px 8px 0px 8px",
        height: isLargeScreen ? "36px" : "32px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        "& .MuiButton-endIcon": {
          marginLeft: 0.5
        }
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
          flex: 1
        }}
      >
        {t("consumer.menu.auctions")}
        {activeAuctionsCount > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "20px",
              width: activeAuctionsCount > 9 ? "auto" : "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: theme.palette.text.primary,
              color: theme.palette.common.white,
              fontSize: (theme) => theme.typography.caption.fontSize,
              fontWeight: theme.typography.fontWeightBold,
              px: activeAuctionsCount > 9 ? 0.75 : 0,
              lineHeight: 1
            }}
          >
            {activeAuctionsCount}
          </Box>
        )}
      </Box>
    </Button>
  ) : null;

  const auctionsDropdownMenu = (
    <Menu
      anchorEl={auctionsMenuAnchor}
      open={auctionsMenuOpen}
      onClose={handleAuctionsMenuClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left"
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: 400,
            width: 320,
            mt: 1
          }
        }
      }}
    >
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          onClick={() => {
            handleAuctionsMenuClose();
            handleNavigation("/my-auctions");
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 0.875,
            px: 2,
            borderRadius: "2rem",
            backgroundColor: theme.palette.misc.timelineBackground,
            border: "1px solid",
            borderColor: theme.palette.misc.coldBackground,
            transition: "all 0.2s ease",
            cursor: "pointer",
            "&:hover": {
              backgroundColor: theme.palette.misc.coldBackground,
              borderColor: theme.palette.semantic.primary.main,
              cursor: "pointer"
            }
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: theme.typography.fontWeightSemibold,
              color: theme.palette.text.primary,
              textAlign: "center",
              pointerEvents: "none"
            }}
          >
            {t("myAuctions.viewAll")}
          </Typography>
        </Box>
        <Box
          onClick={handleNewAuctionClick}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            py: 0.875,
            px: 2,
            borderRadius: "2rem",
            backgroundColor: isEmailVerified
              ? theme.palette.semantic.primary.main
              : theme.palette.action.disabledBackground,
            transition: "all 0.2s ease",
            opacity: isEmailVerified ? 1 : 0.6,
            cursor: isEmailVerified ? "pointer" : "not-allowed",
            "&:hover": {
              backgroundColor: isEmailVerified
                ? theme.palette.semantic.primary.dark
                : theme.palette.action.disabledBackground,
              transform: isEmailVerified ? "translateY(-1px)" : "none",
              boxShadow: isEmailVerified
                ? `0 2px 10px ${theme.palette.divider}`
                : "none",
              cursor: isEmailVerified ? "pointer" : "not-allowed"
            }
          }}
        >
          <AddIcon
            sx={{
              fontSize: 16,
              color: theme.palette.semantic.primary.contrastText,
              pointerEvents: "none"
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: theme.typography.fontWeightBold,
              color: theme.palette.semantic.primary.contrastText,
              textAlign: "center",
              pointerEvents: "none"
            }}
          >
            {t("myAuctions.addNewAuction")}
          </Typography>
        </Box>
      </Box>
      <Divider />
      {groupedAuctions &&
        groupedAuctions.active.length > 0 && [
          <Box key="active-header" sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.secondary
              }}
            >
              {t("myAuctions.active")}
            </Typography>
          </Box>,
          ...groupedAuctions.active.map((auction) => (
            <MenuItem
              key={auction.id}
              onClick={() => {
                handleAuctionsMenuClose();
                handleNavigation(`/my-auctions/${auction.id}`);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  py: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: theme.typography.fontWeightMedium }}
                >
                  {getAuctionLabel(auction)}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getStateLabel(auction)}
                    size="small"
                    sx={getAuctionChipSx(auction)}
                  />
                </Box>
              </Box>
            </MenuItem>
          )),
          <Divider key="active-divider" />
        ]}
      {groupedAuctions &&
        groupedAuctions.accepted.length > 0 && [
          <Box key="accepted-header" sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.secondary
              }}
            >
              {t("myAuctions.acceptedAuctions")}
            </Typography>
          </Box>,
          ...groupedAuctions.accepted.map((auction) => (
            <MenuItem
              key={auction.id}
              onClick={() => {
                handleAuctionsMenuClose();
                handleNavigation(`/my-auctions/${auction.id}`);
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  py: 0.5
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: theme.typography.fontWeightMedium }}
                >
                  {getAuctionLabel(auction)}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={getStateLabel(auction)}
                    size="small"
                    sx={getAuctionChipSx(auction)}
                  />
                </Box>
              </Box>
            </MenuItem>
          )),
          <Divider key="accepted-divider" />
        ]}
      {groupedAuctions &&
        groupedAuctions.draft.length > 0 && [
          <Box key="draft-header" sx={{ px: 2, py: 1 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.text.secondary
              }}
            >
              {t("myAuctions.drafts")}
            </Typography>
          </Box>,
          ...groupedAuctions.draft.map((auction) => {
            const vehicleType =
              auction.auctionItem?.vehicle?.vehicleType ?? VehicleType.CAR;
            const editPath = getAuctionEditRoute(vehicleType);
            return (
              <MenuItem
                key={auction.id}
                onClick={() => {
                  handleAuctionsMenuClose();
                  handleNavigation(editPath);
                }}
                sx={{ position: "relative" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    py: 0.5,
                    pr: 4
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: theme.typography.fontWeightMedium }}
                  >
                    {getAuctionLabel(auction)}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={getStateLabel(auction)}
                      size="small"
                      sx={getAuctionChipSx(auction)}
                    />
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(e, auction.id);
                  }}
                  sx={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: theme.palette.semantic.error.main,
                    "&:hover": {
                      bgcolor: theme.palette.semantic.error.main,
                      color: theme.palette.semantic.error.contrastText
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </MenuItem>
            );
          }),
          <Divider key="draft-divider" />
        ]}
      {(!groupedAuctions ||
        (groupedAuctions.active.length === 0 &&
          groupedAuctions.draft.length === 0 &&
          groupedAuctions.accepted.length === 0)) && (
        <MenuItem disabled>
          <ListItemText>
            {t(
              "myAuctions.noActiveOrDraftAuctions",
              "Ei aktiivisia tai luonnoksia"
            )}
          </ListItemText>
        </MenuItem>
      )}
    </Menu>
  );

  const headerItemUserSettings = (
    <MenuItem
      id="consumerMenuItemUserSettings"
      onClick={() => {
        handleMenuClose();
        handleNavigation("/user-settings");
      }}
    >
      {isLargeScreen && (
        <ListItemIcon>
          <SettingsIcon fontSize="small" />
        </ListItemIcon>
      )}
      <ListItemText>{t("consumer.menu.userSettings")}</ListItemText>
    </MenuItem>
  );

  const headerItemLogOut = (
    <MenuItem
      id="consumerMenuItemLogout"
      onClick={() => {
        handleMenuClose();
        setNotification(null);
        logout();
      }}
    >
      {isLargeScreen && (
        <ListItemIcon>
          <LogoutIcon fontSize="small" sx={{ ml: 0.25 }} />
        </ListItemIcon>
      )}
      <ListItemText>{t("consumer.menu.logout")}</ListItemText>
    </MenuItem>
  );

  //Authenticated user hamburger menu desktop items
  const headerItemUserMenu = (
    <>
      <IconButton
        sx={{
          mr: 2,
          backgroundColor: menuOpen
            ? theme.palette.misc.outlineBorder
            : undefined
        }}
        onClick={(e) => setConsumerMenuToggleButton(e.currentTarget)}
      >
        <AccountCircleIcon
          sx={{ color: theme.palette.semantic.primary.contrastText }}
        />
      </IconButton>
      <Menu
        anchorEl={consumerMenuToggleButtonElement}
        open={menuOpen}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        onClose={handleMenuClose}
      >
        <MenuList
          sx={{
            ".MuiListItemText-primary": {
              cursor: "pointer"
            }
          }}
        >
          <MenuItem
            sx={{
              "&:hover": {
                backgroundColor: "transparent",
                cursor: "default"
              }
            }}
          >
            {isAuthenticated && !isAuthenticating && headerItemUserInfo}
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          {headerItemUserSettings}
          {headerItemArticles}
          {headerItemFAQ}
          {headerItemContactInfo}
          {headerItemLogOut}
        </MenuList>
      </Menu>
    </>
  );

  return (
    <Box>
      <AppBar position="relative" id="consumerHeaderMenu">
        <Toolbar sx={{ justifyContent: "space-between" }}>
          {consumerHeaderLogoButton}
          {/* Consumer desktop header view*/}
          {isLargeScreen ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center"
              }}
            >
              {!isAuthenticating && (
                <Box
                  id="consumerMenu"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    "& .MuiMenuItem-root": {
                      textTransform: "uppercase",
                      fontWeight: theme.typography.fontWeightBold
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: isLargeScreen && "row"
                    }}
                  >
                    {!isAuthenticated ? (
                      <>
                        {headerItemFAQ}
                        {headerItemArticles}
                        {headerItemContactInfo}
                        {headerItemNavToDealerSite}
                        {headerItemLogin}
                        <LanguageSwitcher />
                      </>
                    ) : (
                      <>
                        <Box
                          sx={{
                            mr: 1,
                            my: "auto",
                            display: "flex",
                            gap: 1,
                            alignItems: "center"
                          }}
                        >
                          {isAuthenticated && !isLoadingUserAuctions && (
                            <>{consumerOwnAuctionsButton}</>
                          )}
                        </Box>
                        {isAuthenticated &&
                          !isLoadingUserAuctions &&
                          auctionsDropdownMenu}
                        <NotificationMenu />
                        <LanguageSwitcher />
                        {headerItemUserMenu}
                      </>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <Box style={{ display: "flex", alignItems: "center", gap: 1 }}>
              {!isAuthenticated && headerItemLogin}
              {isAuthenticated && !isLoadingUserAuctions && (
                <>{consumerOwnAuctionsButton}</>
              )}
              {isAuthenticated &&
                !isLoadingUserAuctions &&
                auctionsDropdownMenu}

              <IconButton
                color="inherit"
                aria-label="menu"
                onClick={handleMenuOpen}
              >
                <Badge
                  badgeContent={!menuOpen ? unreadNotifications : undefined}
                  variant="standard"
                  color="secondary"
                >
                  <MenuIcon />
                </Badge>
              </IconButton>
            </Box>
          )}
        </Toolbar>
        {/* Consumer Mobile header header */}
        {!isLargeScreen && (
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Menu
              id="consumerMenu"
              anchorEl={consumerMenuToggleButtonElement}
              open={menuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right"
              }}
              sx={{
                ".MuiPopover-paper": { borderRadius: "6px" },
                width: "250px",
                top: "38px"
              }}
              slotProps={{
                list: {
                  "aria-labelledby": "consumerMenuToggleButton",
                  sx: {
                    display: "flex",
                    flexDirection: "column"
                  }
                }
              }}
            >
              <MenuList sx={{ p: 0 }}>
                {!isAuthenticated ? (
                  <Box>
                    {headerItemNavToDealerSite}
                    {isInUnauthWizard ? <Divider /> : ""}
                    {headerItemFAQ}
                    {headerItemArticles}
                    {headerItemContactInfo}
                    <LanguageSwitcher />
                  </Box>
                ) : (
                  <Box>
                    {headerItemUserInfo}
                    <Divider sx={{ my: 1 }} />
                    {headerMobileItemNotification}
                    <Divider />
                    {headerItemUserSettings}
                    {headerItemArticles}
                    {headerItemFAQ}
                    {headerItemContactInfo}
                    {headerItemLogOut}
                    <LanguageSwitcher />
                  </Box>
                )}
              </MenuList>
            </Menu>
          </Box>
        )}
      </AppBar>
      <DraftAuctionModal
        open={showDraftModal}
        onClose={() => setShowDraftModal(false)}
        draftAuction={draftAuction}
        onContinue={handleContinueDraft}
        onStartNew={handleStartNew}
        showContinueButton={true}
        showStartNewButton={true}
      />
      {/* Delete Confirmation Modal */}
      <DeleteDraftAuctionConfirmationModal
        open={deleteModalAuctionId !== null}
        isDeleting={isDeleting}
        onClose={() => setDeleteModalAuctionId(null)}
        onConfirm={handleDeleteConfirm}
      />
    </Box>
  );
};

export default ConsumerHeader;
